import React, { useState, useEffect } from 'react'
import { apiRequest } from '../utils/api';

const FormulaManager = () => {
  const [activeTab, setActiveTab] = useState('tcs');
  const [formulas, setFormulas] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [currentFormulas, setCurrentFormulas] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFormula, setActiveFormula] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    likes_multiplier: 1.0,
    comments_multiplier: 3.0,
    reposts_multiplier: 3.0,
    shares_multiplier: 3.0,
    weighted_exponent: 2.0,
    divisor_multiplier: 1.0,
    final_multiplier: 100.0,
    exceptional_threshold: 75.0,
    excellent_threshold: 50.0,
    good_threshold: 25.0,
    borderline_threshold: 10.0
  });

  const [thresholdData, setThresholdData] = useState({
    metric: '',
    excellent: 0,
    good: 0,
    okay: 0,
    better_direction: 'higher'
  });

  useEffect(() => {
    fetchFormulas();
    fetchCurrentFormulas();
  }, []);

  const fetchFormulas = async () => {
    try {
      const response = await apiRequest('/tcs-formulas');
      const data = await response.json();
      if (data.success) {
        setFormulas(data.formulas);
        const currentActive = data.formulas.find(f => f.is_active);
        setActiveFormula(currentActive || null);
      }
    } catch (error) {
      console.error('Error fetching TCS formulas:', error);
    }
  };

  const fetchThresholds = async () => {
    try {
      const response = await apiRequest('/thresholds');
      const data = await response.json();
      if (data.success) {
        setThresholds(data.thresholds);
      }
    } catch (error) {
      console.error('Error fetching thresholds:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentFormulas = async () => {
    try {
      const response = await apiRequest('/formulas/current');
      const data = await response.json();
      if (data.success) {
        setCurrentFormulas(data);
      }
    } catch (error) {
      console.error('Error fetching current formulas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || value
    });
  };

  const handleThresholdChange = (e) => {
    const { name, value } = e.target;
    setThresholdData({
      ...thresholdData,
      [name]: name === 'better_direction' ? value : parseFloat(value) || value
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      likes_multiplier: 1.0,
      comments_multiplier: 3.0,
      reposts_multiplier: 3.0,
      shares_multiplier: 3.0,
      weighted_exponent: 2.0,
      divisor_multiplier: 1.0,
      final_multiplier: 100.0,
      exceptional_threshold: 75.0,
      excellent_threshold: 50.0,
      good_threshold: 25.0,
      borderline_threshold: 10.0
    });
    setIsEditing(false);
    setIsCreating(false);
  };

  const resetThresholdForm = () => {
    setThresholdData({
      metric: '',
      excellent: 0,
      good: 0,
      okay: 0,
      better_direction: 'higher'
    });
  };

  const handleCreate = () => {
    resetForm();
    setIsCreating(true);
    setIsEditing(false);
  };

  const handleEdit = (formula) => {
    setFormData(formula);
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleEditThreshold = (threshold) => {
    setThresholdData(threshold);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Please enter a formula name');
      return;
    }

    try {
      const url = isEditing 
        ? `/tcs-formulas/${formData.id}`
        : '/tcs-formulas';
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (result.success) {
        alert(isEditing ? 'Formula updated successfully!' : 'Formula created successfully!');
        resetForm();
        await fetchFormulas();
        await fetchCurrentFormulas();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error saving formula');
    }
  };

  const handleThresholdSubmit = async (e) => {
    e.preventDefault();
    
    if (!thresholdData.metric.trim()) {
      alert('Please enter a metric name');
      return;
    }

    try {
      const response = await apiRequest('/thresholds', {
        method: 'POST',
        body: JSON.stringify(thresholdData),
      });

      const result = await response.json();
      if (result.success) {
        alert('Threshold updated successfully!');
        resetThresholdForm();
        await fetchCurrentFormulas();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error saving threshold');
    }
  };

  const handleDelete = async (formulaId) => {
    if (window.confirm('Are you sure you want to delete this formula?')) {
      try {
        const response = await apiRequest(`/tcs-formulas/${formulaId}`, {
          method: 'DELETE',
        });

        const result = await response.json();
        if (result.success) {
          alert('Formula deleted successfully!');
          await fetchFormulas();
        } else {
          alert('Error: ' + result.error);
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error deleting formula');
      }
    }
  };

  const handleActivate = async (formulaId) => {
    try {
      const response = await apiRequest(`/tcs-formulas/${formulaId}/activate`, {
        method: 'POST',
      });

      const result = await response.json();
      if (result.success) {
        alert('Formula activated successfully!');
        await fetchFormulas();
        await fetchCurrentFormulas();
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error activating formula');
    }
  };

  const calculatePreviewTCS = () => {
    const sampleData = {
      likes: 100,
      comments: 20,
      reposts: 5,
      shares: 10,
      views: 10000,
      amount: 1000
    };

    const weightedEngagements = (
      sampleData.likes * formData.likes_multiplier +
      sampleData.comments * formData.comments_multiplier +
      sampleData.reposts * formData.reposts_multiplier +
      sampleData.shares * formData.shares_multiplier
    );

    const divisor = sampleData.views * sampleData.amount;
    const tcs = ((Math.pow(weightedEngagements, formData.weighted_exponent)) / divisor) * formData.final_multiplier;

    return Math.round(tcs);
  };

  const getVerdictColor = (verdict) => {
    switch (verdict?.toLowerCase()) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'okay': return 'bg-yellow-100 text-yellow-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading formulas and thresholds...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Formula Manager</h2>
          <p className="text-gray-600">Manage all formulas, thresholds, and verdicts for campaign analysis</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('current')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'current'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Current Status
          </button>
          <button
            onClick={() => setActiveTab('tcs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tcs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            TCS Formulas
          </button>
          <button
            onClick={() => setActiveTab('thresholds')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'thresholds'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Verdict Thresholds
          </button>
        </nav>
      </div>

      {/* Current Status Tab */}
      {activeTab === 'current' && (
        <div className="space-y-6">
          {/* Current Active TCS Formula */}
          {currentFormulas?.active_tcs_formula && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-900">
                      Currently Active TCS Formula
                    </h3>
                    <p className="text-sm text-green-700">
                      This formula is currently being used for TCS calculations
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-900">{currentFormulas.active_tcs_formula.name}</div>
                  <div className="text-sm text-green-600">
                    {`(likes × ${currentFormulas.active_tcs_formula.likes_multiplier}) + (comments × ${currentFormulas.active_tcs_formula.comments_multiplier}) + (reposts × ${currentFormulas.active_tcs_formula.reposts_multiplier}) + (shares × ${currentFormulas.active_tcs_formula.shares_multiplier})^${currentFormulas.active_tcs_formula.weighted_exponent} / (views × cost) × ${currentFormulas.active_tcs_formula.final_multiplier}`}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Verdict Thresholds */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Current Verdict Thresholds</h3>
              <p className="text-sm text-gray-600 mt-1">These thresholds are currently being used for verdict calculations</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* TCS */}
                {currentFormulas?.current_thresholds?.['TCS'] && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3">TCS Score</h4>
                    <div className="space-y-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')}`}>
                        Excellent: {currentFormulas.current_thresholds['TCS'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['TCS'].excellent}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')}`}>
                        Good: {currentFormulas.current_thresholds['TCS'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['TCS'].good}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')}`}>
                        Okay: {currentFormulas.current_thresholds['TCS'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['TCS'].okay}
                      </div>
                    </div>
                  </div>
                )}

                {/* ER% */}
                {currentFormulas?.current_thresholds?.['ER%'] && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-3">Engagement Rate %</h4>
                    <div className="space-y-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')}`}>
                        Excellent: {currentFormulas.current_thresholds['ER%'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['ER%'].excellent}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')}`}>
                        Good: {currentFormulas.current_thresholds['ER%'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['ER%'].good}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')}`}>
                        Okay: {currentFormulas.current_thresholds['ER%'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['ER%'].okay}
                      </div>
                    </div>
                  </div>
                )}

                {/* CPV */}
                {currentFormulas?.current_thresholds?.['CPV'] && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-3">Cost Per View</h4>
                    <div className="space-y-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')}`}>
                        Excellent: {currentFormulas.current_thresholds['CPV'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['CPV'].excellent}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')}`}>
                        Good: {currentFormulas.current_thresholds['CPV'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['CPV'].good}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')}`}>
                        Okay: {currentFormulas.current_thresholds['CPV'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['CPV'].okay}
                      </div>
                    </div>
                  </div>
                )}

                {/* CPE */}
                {currentFormulas?.current_thresholds?.['CPE'] && (
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-3">Cost Per Engagement</h4>
                    <div className="space-y-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')}`}>
                        Excellent: {currentFormulas.current_thresholds['CPE'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['CPE'].excellent}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')}`}>
                        Good: {currentFormulas.current_thresholds['CPE'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['CPE'].good}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')}`}>
                        Okay: {currentFormulas.current_thresholds['CPE'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['CPE'].okay}
                      </div>
                    </div>
                  </div>
                )}

                {/* Cost per Comment */}
                {currentFormulas?.current_thresholds?.['Cost per Comment'] && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <h4 className="font-semibold text-red-900 mb-3">Cost per Comment</h4>
                    <div className="space-y-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')}`}>
                        Excellent: {currentFormulas.current_thresholds['Cost per Comment'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Comment'].excellent}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')}`}>
                        Good: {currentFormulas.current_thresholds['Cost per Comment'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Comment'].good}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')}`}>
                        Okay: {currentFormulas.current_thresholds['Cost per Comment'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Comment'].okay}
                      </div>
                    </div>
                  </div>
                )}

                {/* Cost per Share */}
                {currentFormulas?.current_thresholds?.['Cost per Share'] && (
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <h4 className="font-semibold text-indigo-900 mb-3">Cost per Share</h4>
                    <div className="space-y-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')}`}>
                        Excellent: {currentFormulas.current_thresholds['Cost per Share'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Share'].excellent}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')}`}>
                        Good: {currentFormulas.current_thresholds['Cost per Share'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Share'].good}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')}`}>
                        Okay: {currentFormulas.current_thresholds['Cost per Share'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Share'].okay}
                      </div>
                    </div>
                  </div>
                )}

                {/* Cost per Repost */}
                {currentFormulas?.current_thresholds?.['Cost per Repost'] && (
                  <div className="bg-pink-50 rounded-lg p-4">
                    <h4 className="font-semibold text-pink-900 mb-3">Cost per Repost</h4>
                    <div className="space-y-2">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')}`}>
                        Excellent: {currentFormulas.current_thresholds['Cost per Repost'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Repost'].excellent}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')}`}>
                        Good: {currentFormulas.current_thresholds['Cost per Repost'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Repost'].good}
                      </div>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')}`}>
                        Okay: {currentFormulas.current_thresholds['Cost per Repost'].better_direction === 'higher' ? '>' : '<'} {currentFormulas.current_thresholds['Cost per Repost'].okay}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('tcs')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h2a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit TCS Formulas
              </button>
              <button
                onClick={() => setActiveTab('thresholds')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Edit Verdict Thresholds
              </button>
              <button
                onClick={fetchCurrentFormulas}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TCS Formulas Tab */}
      {activeTab === 'tcs' && (
        <>
          {/* Currently Active Formula Display */}
          {activeFormula && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-900">
                      Currently Active Formula
                    </h3>
                    <p className="text-sm text-green-700">
                      This is the formula currently being used by the backend for TCS calculations
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-900">{activeFormula.name}</div>
                  <div className="text-sm text-green-600">
                    {`(likes × ${activeFormula.likes_multiplier}) + (comments × ${activeFormula.comments_multiplier}) + (reposts × ${activeFormula.reposts_multiplier}) + (shares × ${activeFormula.shares_multiplier})^${activeFormula.weighted_exponent} / (views × cost) × ${activeFormula.final_multiplier}`}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formula List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Existing Formulas</h3>
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Formula
                </button>
              </div>
              <div className="divide-y divide-gray-200">
                {formulas.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="text-gray-500">No formulas found</p>
                  </div>
                ) : (
                  formulas.map((formula) => (
                    <div key={formula.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {formula.name}
                            </h4>
                            {formula.is_active && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate mt-1">
                            {formula.description || 'No description'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {!formula.is_active && (
                            <button
                              onClick={() => handleActivate(formula.id)}
                              className="text-green-600 hover:text-green-800 text-xs"
                            >
                              Activate
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(formula)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h2a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {!formula.is_active && (
                            <button
                              onClick={() => handleDelete(formula.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Formula Editor */}
            <div>
              {(isEditing || isCreating) ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      {isEditing ? 'Edit Formula' : 'Create New Formula'}
                    </h3>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Formula Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="e.g., Enhanced Engagement Score"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Describe this formula and its purpose..."
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Engagement Multipliers */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Engagement Weight Multipliers</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Likes ❤️
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="likes_multiplier"
                            value={formData.likes_multiplier}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Comments 💬
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="comments_multiplier"
                            value={formData.comments_multiplier}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reposts ↗️
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="reposts_multiplier"
                            value={formData.reposts_multiplier}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Shares 🔄
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="shares_multiplier"
                            value={formData.shares_multiplier}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Formula Components */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Formula Components</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Weighted Exponent
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="weighted_exponent"
                            value={formData.weighted_exponent}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Power for weighted engagements</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Final Multiplier
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="final_multiplier"
                            value={formData.final_multiplier}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Overall scaling factor</p>
                        </div>
                      </div>
                    </div>

                    {/* TCS Thresholds */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">TCS Score Thresholds</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            🌟 Exceptional
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="exceptional_threshold"
                            value={formData.exceptional_threshold}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ✅ Excellent
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="excellent_threshold"
                            value={formData.excellent_threshold}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            🟢 Good
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="good_threshold"
                            value={formData.good_threshold}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ⚠️ Borderline
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            name="borderline_threshold"
                            value={formData.borderline_threshold}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Formula Preview */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-md font-medium text-gray-900 mb-3">Formula Preview</h4>
                      <div className="text-sm text-gray-600 mb-3">
                        <strong>Formula:</strong> ((likes × {formData.likes_multiplier}) +(comments × {formData.comments_multiplier}) +(reposts × {formData.reposts_multiplier}) +(shares × {formData.shares_multiplier}))^{formData.weighted_exponent} / (views × cost) × {formData.final_multiplier}
                      </div>
                      <div className="text-sm text-gray-600 mb-3">
                        <strong>Sample Calculation:</strong> 
                        With 100 likes, 20 comments, 5 reposts, 10 shares, 10,000 views, ₹1,000 cost → TCS Score: <strong>{calculatePreviewTCS()}</strong>
                      </div>
                      <div className="text-sm text-gray-600">
                        <strong>Verdict Thresholds:</strong>
                        🌟 Exceptional: >{formData.exceptional_threshold}, ✅ Excellent: >{formData.excellent_threshold}, 
                        🟢 Good: >{formData.good_threshold}, ⚠️ Borderline: >{formData.borderline_threshold}
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {isEditing ? 'Update Formula' : 'Create Formula'}
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                  <div className="text-center">
                    <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Manage TCS Formulas</h3>
                    <p className="text-gray-500 mb-6">
                      Create and customize your True Customer Score calculation formulas.
                      Adjust engagement weights, thresholds, and formula components to match your business needs.
                    </p>
                    <button
                      onClick={handleCreate}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Your First Formula
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Verdict Thresholds Tab */}
      {activeTab === 'thresholds' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Thresholds List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Current Verdict Thresholds</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {!currentFormulas?.current_thresholds || Object.keys(currentFormulas.current_thresholds).length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-500">No thresholds found</p>
                </div>
              ) : (
                Object.values(currentFormulas.current_thresholds).map((threshold) => (
                  <div key={threshold.metric} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900">
                          {threshold.metric}
                        </h4>
                        <div className="mt-2 flex space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')}`}>
                            Excellent: {threshold.better_direction === 'higher' ? '>' : '<'} {threshold.excellent}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')}`}>
                            Good: {threshold.better_direction === 'higher' ? '>' : '<'} {threshold.good}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')}`}>
                            Okay: {threshold.better_direction === 'higher' ? '>' : '<'} {threshold.okay}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleEditThreshold(threshold)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h2a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Threshold Editor */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Edit Threshold</h3>
            </div>
            
            <form onSubmit={handleThresholdSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Metric Name *
                </label>
                <input
                  type="text"
                  name="metric"
                  value={thresholdData.metric}
                  onChange={handleThresholdChange}
                  required
                  placeholder="e.g., ER%, CPV, CPE"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Better Direction
                </label>
                <select
                  name="better_direction"
                  value={thresholdData.better_direction}
                  onChange={handleThresholdChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="higher">Higher is Better</option>
                  <option value="lower">Lower is Better</option>
                </select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Excellent
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="excellent"
                    value={thresholdData.excellent}
                    onChange={handleThresholdChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Good
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="good"
                    value={thresholdData.good}
                    onChange={handleThresholdChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Okay
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="okay"
                    value={thresholdData.okay}
                    onChange={handleThresholdChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Threshold Preview</h4>
                <div className="text-sm text-gray-600">
                  <strong>{thresholdData.metric}:</strong>
                  <div className="mt-2 space-y-1">
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('excellent')} mr-2`}>
                      Excellent: {thresholdData.better_direction === 'higher' ? '>' : '<'} {thresholdData.excellent}
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('good')} mr-2`}>
                      Good: {thresholdData.better_direction === 'higher' ? '>' : '<'} {thresholdData.good}
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('okay')} mr-2`}>
                      Okay: {thresholdData.better_direction === 'higher' ? '>' : '<'} {thresholdData.okay}
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor('poor')}`}>
                      Poor: {thresholdData.better_direction === 'higher' ? '<' : '>'} {thresholdData.okay}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetThresholdForm}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Update Threshold
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormulaManager;
